import { useEffect, useRef } from 'react';
import { listMissingIdentifiers } from '../utils/missingIdentifiers';
import {
  hasRequiredDob,
  hasRequiredId,
  hasRequiredServices,
} from '../utils/entryRequirements';
import {
  trackMissing526Identifiers,
  trackMissingDob,
  trackMissingId,
  trackMissingServices,
  trackPassedEntryGate,
} from '../utils/tracking/entryGateTracking';

/**
 * Reports the entry gate outcome for a signed-in, verified Veteran. Fires once
 * per outcome so re-renders don't duplicate the event.
 *
 * `getIsBdd` is called only when an event fires: isBDD writes to
 * sessionStorage, so it must not run on every render.
 */
const useEntryGateTracking = ({ loggedIn, profile, formId, getIsBdd }) => {
  const lastOutcomeKeyRef = useRef(null);
  const getIsBddRef = useRef(getIsBdd);
  getIsBddRef.current = getIsBdd;

  useEffect(() => {
    if (!loggedIn || !profile.verified) {
      return;
    }

    let outcomeKey = null;
    let trackFn = null;

    if (listMissingIdentifiers(profile)) {
      const identifiers = profile.claims.form526RequiredIdentifierPresence;
      if (identifiers && typeof identifiers === 'object') {
        const missingIdentifiers = Object.keys(identifiers).filter(
          idName => identifiers[idName] === false,
        );
        outcomeKey = `missing_526_identifiers_${missingIdentifiers
          .slice()
          .sort()
          .join('_')}`;
        trackFn = options =>
          trackMissing526Identifiers(missingIdentifiers, options);
      }
    } else if (!hasRequiredDob(profile)) {
      outcomeKey = 'missing_dob';
      trackFn = trackMissingDob;
    } else if (!hasRequiredId(profile)) {
      outcomeKey = 'missing_id';
      trackFn = trackMissingId;
    } else if (!hasRequiredServices(profile)) {
      outcomeKey = 'missing_services';
      trackFn = trackMissingServices;
    } else {
      outcomeKey = 'passed_entry_gate';
      trackFn = trackPassedEntryGate;
    }

    if (outcomeKey && lastOutcomeKeyRef.current !== outcomeKey) {
      trackFn({ formId, isBdd: getIsBddRef.current?.() });
      lastOutcomeKeyRef.current = outcomeKey;
    }
  }, [loggedIn, profile, formId]);
};

export default useEntryGateTracking;
